import { log } from '@/lib/logger'
// Conversations API Service for frontend-react
// Uses centralized configuration from env.ts

import { CONVERSATIONS_API_URL, getConversationsAuthToken } from '@/config/env'
import type { ConversationClient } from '@/types/conversations'

/** Un numero de WhatsApp del cliente, con el nombre que le puso en Ajustes. */
export interface WhatsAppLinePayload {
  phone_number_id: string;
  display_name: string;
  display_phone_number: string;
  is_internal: boolean;
}

/** Uno de los hilos que un contacto tiene con este cliente, por linea. */
export interface WhatsAppThreadPayload {
  id: string;
  phone_number_id: string;
  line: WhatsAppLinePayload | null;
  status: string;
  unread_count: number;
  can_reply: boolean;
}

export interface Conversation {
  id: string;
  user: string;
  client: ConversationClient | unknown;
  user_display_name: string;
  title: string;
  description?: string;
  started_at: string;
  source: string;
  platform: string;
  feedback?: string;
  conversation_instructions?: string;
  channel_user_id?: string;
  channel_conversation_id?: string;
  status: string;
  metadata?: Record<string, unknown>;
  timestamp: string;
  message_count: number;
  unread_count?: number;
  can_reply?: boolean;
  reply_block_reason?: string | null;
  phone_number_id?: string;
  line?: WhatsAppLinePayload | null;
  whatsapp_threads?: WhatsAppThreadPayload[] | null;
  last_message?: {
    text: string;
    sender: string;
    timestamp: string;
  };
  // Tipado como en `conversation-utils`: con `unknown[]` las dos interfaces de
  // Conversation eran incompatibles y no se podia pasar una donde se esperaba
  // la otra.
  user_tags?: Array<{
    id?: string;
    name?: string | null;
    color?: string;
    description?: string;
    tag_type?: string;
  }>;
  conversation_tags?: Array<{
    id?: string;
    name?: string | null;
    color?: string;
    description?: string;
    tag_type?: string;
  }>;
  lead_source?: string;
}

export interface ConversationStatusCounts {
  active?: number
  pending?: number
  needs_review?: number
  completed?: number
  archived?: number
}

export interface ConversationsListResponse {
  results?: Conversation[]
  conversations?: Record<string, Conversation[]>
  total_count?: number
  count?: number
  /** Lo dice el servidor: es la unica senal fiable de que queda otra pagina. */
  has_next?: boolean
  total_pages?: number
  /** Cuantas hay de cada estado con los mismos filtros que la lista (menos el estado). */
  status_counts?: ConversationStatusCounts
  lead_source_filter?: string
  source_filter?: string
  status_filter?: string
  category_filter?: string
  tags_filter?: string
  tag_ids_filter?: string
}

export class ConversationsApiError extends Error {
  // Declarados y asignados a mano: `erasableSyntaxOnly` del tsconfig prohibe
  // los parametros-propiedad del constructor.
  status?: number
  detail?: string

  constructor(message: string, status?: number, detail?: string) {
    super(message)
    this.name = 'ConversationsApiError'
    this.status = status
    this.detail = detail
  }
}

function parseDrfDetail(raw: unknown): string | undefined {
  if (raw == null) return undefined
  if (typeof raw === 'string' && raw.trim()) return raw.trim()
  if (Array.isArray(raw)) {
    const parts: string[] = []
    for (const item of raw) {
      if (typeof item === 'string') parts.push(item)
      else if (item && typeof item === 'object' && 'string' in item) {
        parts.push(String((item as { string?: string }).string))
      }
    }
    if (parts.length) return parts.join(' ')
  }
  return undefined
}

function parseConversationsErrorBody(
  status: number,
  errorText: string,
): { detail?: string; logMessage: string } {
  const trimmed = errorText.trim()
  const isHtml =
    trimmed.startsWith('<!') ||
    trimmed.toLowerCase().includes('<!doctype') ||
    trimmed.toLowerCase().includes('<html')

  if (isHtml) {
    return { logMessage: `HTTP ${status} (non-JSON / HTML body)` }
  }

  let detail: string | undefined
  try {
    const j = JSON.parse(errorText) as Record<string, unknown>
    detail = parseDrfDetail(j.detail)
    if (!detail && j.error !== undefined) {
      if (typeof j.error === 'string') detail = j.error
      else if (typeof j.error === 'object' && j.error !== null && 'message' in j.error) {
        detail = String((j.error as { message?: unknown }).message ?? '')
      }
    }
  } catch {
    if (trimmed.length > 0 && trimmed.length < 400) {
      detail = trimmed
    }
  }

  const logMessage =
    detail !== undefined && detail !== '' ? `HTTP ${status}: ${detail}` : `HTTP ${status}`
  return { detail: detail || undefined, logMessage }
}

/** Mensaje seguro para UI (sin HTML ni cuerpos largos de depuración). */
export function conversationsErrorToUserMessage(error: unknown): string {
  if (error instanceof ConversationsApiError) {
    if (error.detail) return error.detail
    if (error.status === 401) {
      return 'Debes iniciar sesión para usar esta función.'
    }
    if (error.status === 403) {
      return 'No tienes permiso para esta acción.'
    }
    if (error.status === 404) {
      return 'No se encontró el recurso en conversaciones. Si acabas de actualizar el backend, reinicia el servicio «conversations».'
    }
    if (error.status === 0) {
      return 'No hay conexión con el servidor.'
    }
    return 'No se pudo completar la operación. Inténtalo de nuevo.'
  }
  if (error instanceof Error) {
    const m = error.message
    if (m.toLowerCase().includes('<!doctype') || m.toLowerCase().includes('<html')) {
      return 'El servidor de conversaciones respondió de forma inesperada. Comprueba que el API esté en marcha.'
    }
    return m
  }
  return 'Ha ocurrido un error inesperado.'
}

// ==================== CONVERSATION FILTER TYPES ====================

export interface ConversationFilterResponse {
  id: string
  name: string
  icon: string
  icon_color: string
  status: string[]
  channels: string[]
  user_categories: string[]
  tag_ids: string[]
  tag_names: string[]
  lead_sources: string[]
  match_type: 'any' | 'all'
  order: number
  is_active: boolean
  criteria_summary: {
    status_count: number
    channels_count: number
    categories_count: number
    tags_count: number
    lead_sources_count: number
  }
  created_at: string
  updated_at: string
}

export interface CreateFilterRequest {
  name: string
  icon?: string
  icon_color?: string
  status?: string[]
  channels?: string[]
  user_categories?: string[]
  tag_ids?: string[]
  tag_names?: string[]
  lead_sources?: string[]
  match_type?: 'any' | 'all'
  order?: number
}

export interface ApplyFilterResponse {
  filter: {
    id: string
    name: string
    icon: string
    icon_color: string
  }
  results: Conversation[]
  count: number
  page: number
  page_size: number
  total_pages: number
  has_next: boolean
  has_previous: boolean
}

class ConversationsApiClient {
  private baseUrl: string

  constructor() {
    // Use centralized configuration
    this.baseUrl = CONVERSATIONS_API_URL
  }

  /** Join base URL with path without duplicating `/api/v1`. */
  private resolveUrl(endpoint: string): string {
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
    const base = (this.baseUrl || '').trim().replace(/\/+$/, '')
    if (!base) {
      return path
    }
    if (base.endsWith('/api/v1') && path.startsWith('/api/v1/')) {
      return `${base}${path.slice('/api/v1'.length)}`
    }
    return `${base}${path}`
  }

  private getAuthToken(): string {
    // Use centralized auth token helper
    return getConversationsAuthToken()
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    // Agregar timestamp para evitar caché
    const separator = endpoint.includes('?') ? '&' : '?'
    const url = `${this.resolveUrl(endpoint)}${separator}_t=${Date.now()}`

    const config: RequestInit = {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options
    }

    // Add authentication token
    const token = this.getAuthToken()
    if (token) {
      config.headers = {
        ...config.headers,
        'Authorization': `Bearer ${token}`,
      }
    }

    try {
      const response = await fetch(url, config)

      if (!response.ok) {
        const errorText = await response.text()
        const { detail, logMessage } = parseConversationsErrorBody(response.status, errorText)
        throw new ConversationsApiError(logMessage, response.status, detail)
      }

      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json()
        return data
      } else {
        const text = await response.text()
        return text as T
      }
    } catch (error) {
      if (error instanceof ConversationsApiError) {
        throw error
      }
      throw new ConversationsApiError(`Network error: ${error}`, 0)
    }
  }

  // Generic GET method for direct endpoint access
  async get<T>(endpoint: string, options?: { params?: Record<string, unknown> }): Promise<T> {
    let path = endpoint
    if (options?.params) {
      const queryParams = new URLSearchParams()
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.set(key, String(value))
        }
      })
      path = `${endpoint}${endpoint.includes('?') ? '&' : '?'}${queryParams.toString()}`
    }
    return this.request<T>(path)
  }

  // Conversations
  async listConversations(params?: {
    page?: number
    page_size?: number
    status?: string
    source?: string
    category?: string
    tags?: string
    tag_ids?: string
    lead_source?: string
    conversation_id?: string
    phone_number_id?: string
    threads?: 'head' | 'all'
    search?: string
    user_tags?: string
  }) {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.set('page', params.page.toString())
    if (params?.page_size) queryParams.set('page_size', params.page_size.toString())
    if (params?.status) queryParams.set('status', params.status)
    if (params?.source) queryParams.set('source', params.source)
    if (params?.category) queryParams.set('category', params.category)
    if (params?.tags) queryParams.set('tags', params.tags)
    if (params?.tag_ids) queryParams.set('tag_ids', params.tag_ids)
    if (params?.lead_source) queryParams.set('lead_source', params.lead_source)
    if (params?.conversation_id) queryParams.set('conversation_id', params.conversation_id)
    if (params?.phone_number_id) queryParams.set('phone_number_id', params.phone_number_id)
    if (params?.threads) queryParams.set('threads', params.threads)
    if (params?.search) queryParams.set('search', params.search)
    if (params?.user_tags) queryParams.set('user_tags', params.user_tags)

    const endpoint = queryParams.toString() ? `/api/v1/conversations/?${queryParams}` : '/api/v1/conversations/'
    return this.request<ConversationsListResponse>(endpoint)
  }

  async getConversation(conversationId: string) {
    return this.request<Conversation>(`/api/v1/conversations/${conversationId}/`)
  }

  async getConversationsByUser(userId: string) {
    return this.request<ConversationsListResponse>(`/api/v1/conversations/?user=${userId}`)
  }

  async updateConversationStatus(conversationId: string, status: string) {
    return this.request<Conversation>(`/api/v1/conversations/${conversationId}/status/`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    })
  }

  async markAsRead(conversationId: string) {
    return this.request(`/api/v1/conversations/${conversationId}/read/`, {
      method: 'POST'
    })
  }

  async deleteConversation(conversationId: string, deleteUser: boolean = false) {
    return this.request(`/api/v1/conversations/${conversationId}/safe-delete/`, {
      method: 'DELETE',
      body: JSON.stringify({ delete_user: deleteUser })
    })
  }

  async deletePhoneCall(callId: string) {
    return this.request(`/api/v1/phone-calls/calls/${callId}/`, {
      method: 'DELETE'
    })
  }

  async archiveConversation(conversationId: string) {
    return this.request(`/api/v1/conversations/${conversationId}/archive/`, {
      method: 'POST'
    })
  }

  async getConversationMessages(conversationId: string, params?: {
    page?: number
    page_size?: number
  }) {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.set('page', params.page.toString())
    if (params?.page_size) queryParams.set('page_size', params.page_size.toString())

    const endpoint = queryParams.toString()
      ? `/api/v1/conversations/${conversationId}/messages/list/?${queryParams}`
      : `/api/v1/conversations/${conversationId}/messages/list/`
    return this.request(endpoint)
  }

  async getPhoneCallMessages(callId: string) {
    // Use the phone calls API endpoint to get call details with turns
    return this.request(`/api/v1/phone-calls/calls/${callId}/`)
  }

  // Phone user management methods
  async getPhoneUserCategory(userId: string) {
    return this.request(`/api/v1/phone-calls/users/${userId}/category/`)
  }

  async updatePhoneUserCategory(userId: string, category: string) {
    return this.request(`/api/v1/phone-calls/users/${userId}/category/`, {
      method: 'PATCH',
      body: JSON.stringify({ user_category: category })
    })
  }

  async getPhoneUserTags(userId: string) {
    return this.request(`/api/v1/phone-calls/users/${userId}/tags/`)
  }

  async updatePhoneUserTags(userId: string, tagIds: string[]) {
    return this.request(`/api/v1/phone-calls/users/${userId}/tags/`, {
      method: 'PATCH',
      body: JSON.stringify({ tag_ids: tagIds })
    })
  }

  // Phone call feedback methods
  async getPhoneCallFeedback(callId: string) {
    return this.request(`/api/v1/phone-calls/calls/${callId}/feedback/`)
  }

  async updatePhoneCallFeedback(callId: string, feedback: string) {
    return this.request(`/api/v1/phone-calls/calls/${callId}/feedback/`, {
      method: 'PATCH',
      body: JSON.stringify({ feedback })
    })
  }

  // Phone call archive methods
  async archivePhoneCall(callId: string) {
    return this.request(`/api/v1/phone-calls/calls/${callId}/archive/`, {
      method: 'PATCH'
    })
  }

  async unarchivePhoneCall(callId: string) {
    return this.request(`/api/v1/phone-calls/calls/${callId}/unarchive/`, {
      method: 'PATCH'
    })
  }

  async sendMessage(conversationId: string, message: string, sender: 'user' | 'admin' = 'user') {
    return this.request(`/api/v1/conversations/${conversationId}/messages/`, {
      method: 'POST',
      body: JSON.stringify({ text: message, sender })
    })
  }


  async pauseChatbot(conversationId: string, durationMinutes: number | null | "indefinite") {
    return this.request(`/api/v1/conversations/${conversationId}/chatbot/pause/`, {
      method: 'PATCH',
      body: JSON.stringify({ duration_minutes: durationMinutes })
    })
  }

  async resumeChatbot(conversationId: string) {
    // Resume is done by calling pauseChatbot with duration 0
    return this.pauseChatbot(conversationId, 0)
  }

  async getUserCategory(userId: string) {
    const url = `/api/v1/users/${userId}/category/`

    log.debug('🔍 Getting user category from:', this.baseUrl + url)

    try {
      const response = await this.request(url, {
        method: 'GET'
      })

      log.debug('✅ User category response:', response)
      return response
    } catch (error) {
      console.error('❌ Failed to get user category:', error)
      throw error
    }
  }

  async updateUserCategory(userId: string, category: string) {
    const url = `/api/v1/users/${userId}/category/`
    const body = { user_category: category }

    log.debug('🚀 API Call Details:')
    log.debug('  URL:', this.baseUrl + url)
    log.debug('  Method: PATCH')
    log.debug('  Body:', body)
    log.debug('  User ID:', userId)
    log.debug('  Category:', category)

    try {
      const response = await this.request(url, {
        method: 'PATCH',
        body: JSON.stringify(body)
      })

      log.debug('✅ API Response:', response)
      return response
    } catch (error) {
      console.error('❌ API Error:', error)
      throw error
    }
  }

  async createConversationFeedback(
    conversationId: string,
    data: {
      comment?: string;
      rating?: number;
      source?: string;
      message_id?: string;
      conversation_instructions?: string;
    }
  ) {
    return this.request(`/api/v1/conversations/${conversationId}/feedback/`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async getConversationFeedback(conversationId: string, params?: { limit?: number }) {
    const limit = params?.limit ?? 50
    return this.request(`/api/v1/conversations/${conversationId}/feedback/?limit=${limit}`)
  }

  // Backward compatibility: PATCH still works but delegates to structured feedback
  async updateConversationFeedback(conversationId: string, feedback: string) {
    return this.createConversationFeedback(conversationId, { comment: feedback, source: 'legacy_patch' })
  }

  async getConversationInstructions(conversationId: string): Promise<{ conversation_instructions: string }> {
    try {
      const conversation = await this.getConversation(conversationId)
      const direct = typeof conversation?.conversation_instructions === 'string'
        ? conversation.conversation_instructions
        : ''
      if (direct) {
        return { conversation_instructions: direct }
      }
    } catch {
      // Fall back to feedback endpoint path below.
    }

    const fallback = await this.getConversationFeedback(conversationId, { limit: 1 }) as {
      conversation_instructions?: string
    }
    return { conversation_instructions: fallback?.conversation_instructions || '' }
  }

  async saveConversationInstructions(conversationId: string, instructions: string) {
    return this.createConversationFeedback(conversationId, {
      conversation_instructions: instructions.trim(),
    })
  }

  async submitGeneralFeedback(feedback: string) {
    return this.request('/api/v1/feedback/general/', {
      method: 'POST',
      body: JSON.stringify({ feedback }),
    })
  }

  async listGeneralFeedback(limit: number = 50) {
    return this.request(`/api/v1/feedback/general/list/?limit=${limit}`)
  }

  async updateUserTags(userId: string, tagIds: string[]) {
    return this.request(`/api/v1/tags/users/${userId}/`, {
      method: 'PATCH',
      body: JSON.stringify({ tag_ids: tagIds })
    })
  }

  async getUserTags(userId: string) {
    return this.request(`/api/v1/tags/users/${userId}/`, {
      method: 'GET'
    })
  }

  async getGlobalTagPolicy() {
    return this.request(`/api/v1/tags/policy/`, {
      method: 'GET'
    })
  }

  async updateGlobalTagPolicy(policyMode: 'manual_protegido' | 'adaptativo') {
    return this.request(`/api/v1/tags/policy/`, {
      method: 'PATCH',
      body: JSON.stringify({ policy_mode: policyMode }),
    })
  }

  // Tag Management
  async getTags(params?: { type?: string; search?: string; order_by?: string; order?: 'asc' | 'desc' }) {
    const queryParams = new URLSearchParams()
    if (params?.type) queryParams.set('type', params.type)
    if (params?.search) queryParams.set('search', params.search)
    if (params?.order_by) queryParams.set('order_by', params.order_by)
    if (params?.order) queryParams.set('order', params.order)
    const endpoint = queryParams.toString() ? `/api/v1/tags/?${queryParams}` : '/api/v1/tags/'
    return this.request(endpoint)
  }

  async getTag(tagId: string) {
    return this.request(`/api/v1/tags/${tagId}/`)
  }

  async createTag(data: {
    name: string
    color: string
    description?: string
    tag_type?: string
  }) {
    return this.request('/api/v1/tags/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateTag(tagId: string, data: {
    name?: string
    color?: string
    description?: string
  }) {
    return this.request(`/api/v1/tags/${tagId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteTag(tagId: string) {
    return this.request(`/api/v1/tags/${tagId}/`, {
      method: 'DELETE'
    })
  }

  // ==================== CONVERSATION FILTERS ====================

  /**
   * List all saved conversation filters
   */
  async getFilters() {
    return this.request<ConversationFilterResponse[]>('/api/v1/filters/')
  }

  /**
   * Create a new conversation filter
   */
  async createFilter(data: CreateFilterRequest) {
    return this.request<ConversationFilterResponse>('/api/v1/filters/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Update an existing filter
   */
  async updateFilter(filterId: string, data: Partial<CreateFilterRequest>) {
    return this.request<ConversationFilterResponse>(`/api/v1/filters/${filterId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  /**
   * Delete a filter
   */
  async deleteFilter(filterId: string) {
    return this.request(`/api/v1/filters/${filterId}/`, {
      method: 'DELETE'
    })
  }

  /**
   * Apply a filter and get matching conversations
   */
  async applyFilter(filterId: string, params?: { page?: number; page_size?: number }) {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.set('page', params.page.toString())
    if (params?.page_size) queryParams.set('page_size', params.page_size.toString())

    const endpoint = queryParams.toString()
      ? `/api/v1/filters/${filterId}/apply/?${queryParams}`
      : `/api/v1/filters/${filterId}/apply/`
    return this.request<ApplyFilterResponse>(endpoint)
  }

  /**
   * Get count of conversations matching a filter
   */
  async getFilterCount(filterId: string) {
    return this.request<{ filter_id: string; filter_name: string; count: number }>(
      `/api/v1/filters/${filterId}/count/`
    )
  }

  /**
   * Channel catalog for inbox filters (ConversationSource / ChannelRegistry metadata).
   */
  async getChannelCatalog(params?: { tier?: 'all' | 'primary_default' | 'available' | 'hidden' }) {
    const tier = params?.tier ?? 'all'
    return this.get<{ channels: ChannelCatalogEntry[] }>('/api/v1/channels/', {
      params: tier === 'all' ? undefined : { tier },
    })
  }

  // ==================== CONVERSATION SOURCES (custom channels, staff writes) ====================

  async listConversationSources(params?: { enabled?: boolean }): Promise<ConversationSource[]> {
    const data = await this.get<ConversationSource[] | { results: ConversationSource[] }>(
      '/api/v1/conversation-sources/',
      {
        params:
          params?.enabled === true
            ? { enabled: 'true' }
            : params?.enabled === false
              ? { enabled: 'false' }
              : undefined,
      },
    )
    if (Array.isArray(data)) {
      return data
    }
    if (data && typeof data === 'object' && 'results' in data && Array.isArray(data.results)) {
      return data.results
    }
    return []
  }

  async createConversationSource(body: ConversationSourceCreatePayload): Promise<ConversationSource> {
    return this.request<ConversationSource>('/api/v1/conversation-sources/', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async patchConversationSource(
    id: string,
    body: Partial<ConversationSourceCreatePayload>,
  ): Promise<ConversationSource> {
    return this.request<ConversationSource>(`/api/v1/conversation-sources/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
  }

  async deleteConversationSource(id: string): Promise<void> {
    await this.request(`/api/v1/conversation-sources/${id}/`, {
      method: 'DELETE',
    })
  }
}

/** One row from GET/PATCH /api/v1/conversation-sources/ */
export interface ConversationSource {
  id: string
  key: string
  display_name: string
  description?: string
  icon?: string
  filter_tier: 'primary_default' | 'available' | 'hidden'
  supports_internal_rag_agent: boolean
  persistence_for_external_invoke: boolean
  is_system: boolean
  is_enabled: boolean
  metadata?: Record<string, unknown> | null
  created_at?: string
  updated_at?: string
}

export type ConversationSourceCreatePayload = {
  key: string
  display_name: string
  description?: string
  icon?: string
  filter_tier?: 'primary_default' | 'available' | 'hidden'
  supports_internal_rag_agent?: boolean
  persistence_for_external_invoke?: boolean
  is_enabled?: boolean
  metadata?: Record<string, unknown>
}

/** One row from GET /api/v1/channels/ */
export interface ChannelCatalogEntry {
  key: string
  label: string
  display_name: string
  icon: string
  description: string
  filter_tier: 'primary_default' | 'available' | 'hidden'
  supports_internal_rag_agent: boolean
  persistence_for_external_invoke: boolean
  is_system?: boolean
}

// Export singleton instance
export const conversationsApi = new ConversationsApiClient()
